import { AuthService } from "@js/services/AuthService";
import { OrganisationService } from "@js/services/OrganisationService";
import { RoleService } from "@js/services/RoleService";
import { SelectListService } from "@js/services/SelectListService";
import { Utils } from "@js/Utils";
import {
  ApplicationUser,
  ApplicationUserClient,
  ApplicationUserCrudClient,
  AuthClient,
  FileAttachmentClient,
  IntroducerClient,
  IntroducerSimpleDTO,
  LocationEnum,
  Organisation,
  UserClient,
} from "@src/apis/ModularClient";
import { FileAttachmentHelper } from "@js/helpers/FileAttachmentHelper";
import { Expand, getOneUsingOData } from "@ccq/ts-lib";
import { IndexService } from "@js/services/IndexService";

export class ProfileController {
  profile: Expand<ApplicationUser> = null;
  profileEdit: Expand<ApplicationUser> = null;

  // Booleans to control which fields to display in the edit modal
  showEditModal: boolean = false;
  isEditIntro: boolean = false;
  isEditMiniBio: boolean = false;
  isEditOrganisation: boolean = false;
  isEditPassword: boolean = false;
  isEditContactPrefs: boolean = false;
  isEditSystemSettings: boolean = false;

  // Edit password
  currentPassword: string;
  newPassword: string;
  repeatPassword: string;

  // manage errors
  error: boolean = false;
  errorMessage: string;

  editTitle: string = "";

  //get all organisations
  organisations: Organisation[];
  userOrganisation: Organisation;

  // This is the toggle to open the file upload modal
  openModal: boolean = false;

  profileForm: FormData;

  fullAvatarUrl: string = "";

  loggedIn: boolean = false;

  tinymceOptions: any;
  showSystemSetting: boolean = false;

  referrerOptions = [];
  locationOptions = [];

  static $inject = [
    "$scope",
    "$http",
    "AuthService",
    "AuthClient",
    "UserClient",
    "FileAttachmentHelper",
    "FileAttachmentClient",
    "IntroducerSimpleClient",
    "OrganisationService",
    "RoleService",
    "$location",
    "$cookies",
    "SelectListService",
    "RoleService",
    "ApplicationUserClient",
    "ApplicationUserCrudClient",
    "IndexService",
  ];

  constructor(
    private $scope: ng.IScope,
    private $http: ng.IHttpService,
    private authService: AuthService,
    private authClient: AuthClient,
    private userClient: UserClient,
    private fileAttachmentHelper: FileAttachmentHelper,
    private fileAttachmentClient: FileAttachmentClient,
    private introducerClient: IntroducerClient,
    private organisationService: OrganisationService,
    private $roleService: RoleService,
    private $location: ng.ILocationService,
    private $cookies: ng.cookies.ICookiesService,
    private selectListService: SelectListService,
    private roleService: RoleService,
    private applicationUserClient: ApplicationUserClient,
    private applicationUserCrudClient: ApplicationUserCrudClient,
    private indexService: IndexService,
  ) {
    this.tinymceOptions = {
      content_style: "body {color: #304b9a;font:300 13.33px Roboto,sans-serif;}",
      menubar: false,
      height: 300,
      branding: false,
      statusbar: false,
      plugins: [
        "advlist autolink lists link image charmap print preview anchor",
        "searchreplace visualblocks code fullscreen",
        "insertdatetime media table paste code help wordcount",
      ],
      toolbar:
        "undo redo | formatselect | " +
        "bold italic backcolor forecolor | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | preview fullscreen |" +
        "removeformat | help",
    };

    if (this.$cookies.get("access_token") != null && this.$cookies.get("access_token") != undefined) {
      this.init();
    }

    this.referrerOptions = this.selectListService.GetReferrerOptions();
    this.locationOptions = this.selectListService.GetLocations();
  }

  async init() {
    this.loggedIn = true;

    this.profile = await getOneUsingOData(this.applicationUserCrudClient, {
      filter: { id: { eq: await this.indexService.currentUserId } },
    });

    if (this.$location.path().startsWith("/managepreferences") && this.roleService.IsLender == true) {
      this.editContactPreferences();
    }
    this.profileEdit = Utils.deepCopy(this.profile);
    this.roleService.isOrganiationAdminUser().then((isOrgAdmin) => (this.showSystemSetting = isOrgAdmin));
    this.showSystemSetting = this.profile.isOrganisationAdmin;
    this.getFileURL(this.profile.profilePictureUrl);
    this.organisationService.fetchAll().then((response) => {
      this.organisations = response;

      if (this.profile && this.profile.organisationId) {
        this.userOrganisation = this.organisations.find((x) => x.id == this.profile.organisationId);
      }
    });
  }
  /** Close the edit modal */
  closeEditModal() {
    this.showEditModal = false;
    this.isEditIntro = false;
    this.isEditMiniBio = false;
    this.isEditPassword = false;
    this.isEditContactPrefs = false;
    this.editTitle = "";
    this.isEditOrganisation = false;
    this.isEditSystemSettings = false;

    this.profileEdit = null;
    delete this.currentPassword;
    delete this.newPassword;
    delete this.repeatPassword;
    this.error = false;
  }

  /** Save the profile changes */
  saveProfile() {
    this.applicationUserCrudClient.patch(this.profileEdit).then((patchResponse) => {
      this.profile = patchResponse;

      this.getFileURL(this.profile.profilePictureUrl);
    });

    this.closeEditModal();
  }

  /** Edit the mini bio */
  editMiniBio() {
    this.isEditMiniBio = true;
    this.editTitle = "Edit Mini Bio";
    this.openEditModal();
  }

  /** Edit the intro details */
  editIntro() {
    this.isEditIntro = true;
    this.editTitle = "Edit my Information";
    this.openEditModal();
  }

  /** Edit the default organisation details */
  editDefaultOrganisation() {
    this.isEditOrganisation = true;
    this.editTitle = "Edit default broker details";
    this.openEditModal();
  }

  /** Open the edit modal */
  openEditModal() {
    // Make a copy of the main profile object so that the main page doesn't get updated at the same time
    this.profileEdit = Utils.deepCopy(this.profile);
    this.showEditModal = true;
  }

  /** Returns the location text based on the profile's Location enum */
  getLocationName(): string {
    var locationName: string = "";

    if (this.profile) {
      switch (this.profile.location) {
        case LocationEnum.London:
          locationName = "London";
          break;
        case LocationEnum.NorthWest:
          locationName = "North West";
          break;
        case LocationEnum.Midlands:
          locationName = "Midlands";
          break;
        case LocationEnum.EastofEngland:
          locationName = "East of England";
          break;
        case LocationEnum.SouthEast:
          locationName = "South East";
          break;
        case LocationEnum.SouthWest:
          locationName = "South West";
          break;
        case LocationEnum.NorthEast:
          locationName = "North East";
          break;
        case LocationEnum.Wales:
          locationName = "Wales";
          break;
        case LocationEnum.Scotland:
          locationName = "Scotland";
          break;
        case LocationEnum.NorthernIreland:
          locationName = "Northern Ireland";
          break;
        case LocationEnum.ChannelIsland:
          locationName = "Channel Islands";
          break;
        default:
          locationName = "";
      }
    }

    return locationName;
  }

  /**
   * Called when a profile picture has been selected
   * @param files
   */
  onFileSelect(files) {
    this.fileAttachmentHelper.uploadFileAndReturnURI(files).then((result) => {
      // Make a copy of the main profile object so that the main page doesn't get updated at the same time
      this.profileEdit = Utils.deepCopy(this.profile);
      //this.profileEdit = { ...this.profile };

      this.profileEdit.profilePictureUrl = result.fileLocation;
      this.saveProfile();
      this.getFileURL(this.profileEdit.profilePictureUrl);
    });
  }

  /**
   * Gets the full url for the url specified
   * @param url
   */
  getFileURL(url: string) {
    if (url != null) {
      this.fileAttachmentClient.getAmazonFileAccessUri(url).then((result) => {
        this.fullAvatarUrl = result;
      });
    }
  }

  /**
   * return the default organisation selected
   */
  getSelectedDefaultOrganisationName(id: number) {
    if (this.organisations) {
      var find = this.organisations.find((x) => x.id == id);
    }

    if (find) {
      return find.name;
    } else {
      return "none";
    }
  }

  /** Edit the current password */
  editPassword() {
    this.isEditPassword = true;
    this.editTitle = "Edit password";
    this.openEditModal();
  }

  // change passwords
  async applyPasswordChange() {
    this.error = false;

    if (!(this.newPassword && this.repeatPassword && this.newPassword === this.repeatPassword)) {
      return;
    }

    try {
      await this.authClient.changePassword({ oldPassword: this.currentPassword, newPassword: this.newPassword });
      delete this.currentPassword;
      delete this.newPassword;
      delete this.repeatPassword;

      this.showEditModal = false;
    } catch (e) {
      this.error = true;
      this.errorMessage = "Current password incorrect. Please try again";
    }
  }

  editContactPreferences() {
    this.isEditContactPrefs = true;
    this.editTitle = "Edit Contact Preferences";
    this.openEditModal();
  }

  editSystemSettings() {
    this.isEditSystemSettings = true;
    this.editTitle = "Edit System settings";
    this.openEditModal();
  }

  // We no loger need it as we are not useing plantype for organisation.
  /* isBasicMode() {
            if (this.userOrganisation != null) {
              return  this.userOrganisation.planType == PlanTypeEnum.Basic;
            } else {
               return false;
            }
        }*/

  manageLicence() {
    this.$location.path("/settings");
  }

  viewLicense() {
    this.$location.path("/license/1"); // + this.profile.licenseMasterId);
  }
}
